import React, { useState } from 'react';
import {
  UserCheck,
  CheckCircle2,
  Clock,
  Share2,
  Bookmark,
  Building2,
  ShieldAlert,
  Sliders,
  Sparkles,
  Check
} from 'lucide-react';
import { EditorialProject } from '../../../../../types/EditorialProject.types';

const cn = (...args: any[]) => args.filter(Boolean).join(' ');

interface Phase1Props {
  project: EditorialProject;
  onUpdateProject: (updated: EditorialProject) => void;
  onAdvanceToPhase2: (project: EditorialProject) => void;
}

export default function EditorialPhase1Evaluation({
  project,
  onUpdateProject,
  onAdvanceToPhase2
}: Phase1Props) {
  const [isEditingReport, setIsEditingReport] = useState(false);
  const [reportData, setReportData] = useState({
    narrativeQuality: project.zeroReader?.report?.narrativeQuality || 4,
    commercialViability: project.zeroReader?.report?.commercialViability || 4,
    technicalRigor: project.zeroReader?.report?.technicalRigor || 4,
    catalogFit: project.zeroReader?.report?.catalogFit || 4,
    recommendation: project.zeroReader?.report?.recommendation || 'publicar',
    evaluatorNotes: project.zeroReader?.report?.evaluatorNotes || ''
  });

  const [assigneeName, setAssigneeName] = useState(project.zeroReader?.assignedReader || 'Pedro Sánchez (Comité de Lectura)');
  const [deadlineDate, setDeadlineDate] = useState(project.zeroReader?.deadline || '2026-09-30');
  const [boardDecisionNotes, setBoardDecisionNotes] = useState(project.editorialBoardDecision?.comments || '');

  const hasReport = !!project.zeroReader?.report?.completed;
  const boardDecision = project.editorialBoardDecision?.decision || 'pendiente';

  // Regla 1: no se puede enviar a la Mesa Editorial sin informe de lectura completado
  const canSubmitToBoard = hasReport;

  const handleSaveReport = () => {
    const updated: EditorialProject = {
      ...project,
      zeroReader: {
        assignedReader: assigneeName,
        deadline: deadlineDate,
        report: {
          completed: true,
          narrativeQuality: reportData.narrativeQuality,
          commercialViability: reportData.commercialViability,
          technicalRigor: reportData.technicalRigor,
          catalogFit: reportData.catalogFit,
          recommendation: reportData.recommendation as 'publicar' | 'revisar' | 'descartar',
          evaluatorNotes: reportData.evaluatorNotes || 'Informe evaluado conforme a los estándares de idoneidad y rigor.',
          evaluationDate: new Date().toISOString().split('T')[0]
        }
      }
    };
    onUpdateProject(updated);
    setIsEditingReport(false);
  };

  const handleBoardDecision = (decision: 'aprobado' | 'rechazado' | 'revision') => {
    if (!canSubmitToBoard) return;
    const updated: EditorialProject = {
      ...project,
      editorialBoardDecision: {
        decision,
        decisionDate: new Date().toISOString().split('T')[0],
        comments: boardDecisionNotes || (decision === 'aprobado' ? 'Aprobado por el Comité Editorial para pase a Fase de Contratación.' : 'Resolución emitida por la Mesa Editorial.')
      },
      status: decision === 'aprobado' ? 'planning' : project.status,
      editorialPhase: decision === 'aprobado' ? 'contratacion' : project.editorialPhase
    };
    onUpdateProject(updated);
    if (decision === 'aprobado') onAdvanceToPhase2(updated);
  };

  const toggleCrmSync = () => {
    if (!project.proposalDetails) return;
    onUpdateProject({ ...project, proposalDetails: { ...project.proposalDetails, crmSyncEnabled: !project.proposalDetails.crmSyncEnabled } });
  };

  const togglePresale = () => {
    if (!project.proposalDetails) return;
    onUpdateProject({ ...project, proposalDetails: { ...project.proposalDetails, presaleEnabled: !project.proposalDetails.presaleEnabled } });
  };

  const avgScore = project.zeroReader?.report?.completed
    ? ((project.zeroReader.report.narrativeQuality +
        project.zeroReader.report.commercialViability +
        project.zeroReader.report.technicalRigor +
        project.zeroReader.report.catalogFit) / 4).toFixed(1)
    : null;

  return (
    <div className="ed-grid-12">
      {/* Columna izquierda: registro de la propuesta */}
      <div className="ed-col-4 ed-col-stack">
        <div className="ed-panel">
          <h3 className="ed-panel-title" style={{ marginBottom: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', paddingBottom: '0.75rem' }}>
            <Bookmark size={14} className="ed-icon-green" style={{ color: 'var(--color-brand-green)' }} />
            2.1 Registro_De_Propuesta
          </h3>

          <div className="ed-panel-body">
            <div className="ed-box">
              <span className="ed-label">Tipo de Entrada</span>
              <div className="ed-row-between" style={{ marginTop: '0.25rem' }}>
                <span className="ed-value">
                  {project.proposalDetails?.isSolicited ? 'Propuesta Solicitada (Fondo/Encargo)' : 'Propuesta No Solicitada'}
                </span>
                <span className="ed-badge">{project.proposalDetails?.genre || project.bookCategory}</span>
              </div>
            </div>

            <div className="ed-box">
              <span className="ed-label">Colección Editorial</span>
              <p className="ed-value" style={{ margin: '0.125rem 0 0' }}>{project.proposalDetails?.collection || 'Fondo General Albatross'}</p>
            </div>

            <div className="ed-box">
              <span className="ed-label">Agencia / Representante</span>
              <p className="ed-value" style={{ margin: '0.125rem 0 0' }}>{project.proposalDetails?.agentOrAgency || 'Contacto Directo con Autor'}</p>
            </div>

            <div className="ed-box">
              <span className="ed-label">Público Objetivo</span>
              <p className="ed-text-muted" style={{ margin: '0.125rem 0 0' }}>{project.proposalDetails?.targetAudience || 'Lectores generales y mercado universitario'}</p>
            </div>

            <div className="ed-box">
              <span className="ed-label">Sinopsis Ejecutiva</span>
              <p className="ed-text-muted" style={{ marginTop: '0.25rem', lineHeight: 1.5, fontSize: '0.5625rem' }}>
                {project.proposalDetails?.synopsis || 'Sinopsis inicial pendiente de ampliación por el equipo editorial.'}
              </p>
            </div>

            {/* Conexión con CRM */}
            <div className="ed-connector" style={{ borderColor: 'rgba(56,189,248,0.3)' }}>
              <div className="ed-row-between">
                <span className="ed-connector-title" style={{ color: '#38bdf8' }}>
                  <Share2 size={12} /> Conexión con CRM
                </span>
                <button onClick={toggleCrmSync} className={cn('ed-badge', project.proposalDetails?.crmSyncEnabled ? 'ed-badge--green' : '')}>
                  {project.proposalDetails?.crmSyncEnabled ? 'Sincronizado' : 'Desconectado'}
                </button>
              </div>
              <p className="ed-text-muted" style={{ fontSize: '0.5rem' }}>
                {project.proposalDetails?.crmContactId
                  ? `Vinculado a contacto CRM: ${project.proposalDetails.crmContactId}`
                  : 'Autor / Agencia prioritaria en CRM.'}
              </p>
              <div className="ed-row-between" style={{ borderTop: '1px solid #1e293b', paddingTop: '0.5rem' }}>
                <span style={{ fontSize: '0.5rem', color: '#cbd5e1' }}>Preventa Comercial Anticipada:</span>
                <button onClick={togglePresale} className={cn('ed-badge', project.proposalDetails?.presaleEnabled ? 'ed-badge--green' : '')}>
                  {project.proposalDetails?.presaleEnabled ? 'Habilitada' : 'Inactiva'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Columna central: informe del lector cero */}
      <div className="ed-col-4 ed-col-stack">
        <div className="ed-panel">
          <div className="ed-panel-header">
            <h3 className="ed-panel-title">
              <UserCheck size={14} style={{ color: 'var(--color-brand-green)' }} />
              Informe_De_Lectura
            </h3>
            <button
              onClick={() => setIsEditingReport(!isEditingReport)}
              style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: '0.5625rem', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <Sliders size={11} /> {isEditingReport ? 'Cancelar' : 'Evaluar / Modificar'}
            </button>
          </div>

          <div className="ed-box" style={{ marginBottom: '1rem' }}>
            <div className="ed-row-between" style={{ marginBottom: '0.25rem' }}>
              <span className="ed-label">Lector Cero Asignado</span>
              <span style={{ fontSize: '0.5rem', color: '#38bdf8', fontWeight: 700 }}>Límite: {project.zeroReader?.deadline || '2026-08-30'}</span>
            </div>
            <p className="ed-value">{project.zeroReader?.assignedReader || 'Pedro Sánchez (Director de Lectura)'}</p>
          </div>

          {isEditingReport ? (
            <div className="ed-box" style={{ borderColor: 'rgba(0,171,132,0.3)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ fontSize: '0.5625rem', color: 'var(--color-brand-green)', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.375rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--color-brand-blue-lighter)' }}>
                <Sparkles size={12} /> Plantilla Paramétrica de Evaluación (1 a 5)
              </div>

              <div className="ed-field">
                <label className="ed-field-label">Evaluador Asignado</label>
                <input type="text" value={assigneeName} onChange={(e) => { const v = e.target.value; setAssigneeName(v); }} className="ed-input" />
              </div>

              <div className="ed-col-stack">
                {[
                  { key: 'narrativeQuality', label: '1. Calidad Narrativa / Literaria', val: reportData.narrativeQuality },
                  { key: 'commercialViability', label: '2. Viabilidad Comercial y Ventas', val: reportData.commercialViability },
                  { key: 'technicalRigor', label: '3. Rigor Técnico y Documental', val: reportData.technicalRigor },
                  { key: 'catalogFit', label: '4. Adecuación al Catálogo Albatross', val: reportData.catalogFit }
                ].map((metric) => (
                  <div key={metric.key} className="ed-score-row">
                    <span style={{ fontSize: '0.5625rem', color: '#cbd5e1', fontWeight: 600 }}>{metric.label}</span>
                    <div className="ed-score-buttons">
                      {[1, 2, 3, 4, 5].map((score) => (
                        <button
                          key={score}
                          type="button"
                          onClick={() => setReportData(prev => ({ ...prev, [metric.key]: score }))}
                          className={cn('ed-score-btn', metric.val === score && 'ed-score-btn--active')}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="ed-field">
                <label className="ed-field-label">Dictamen / Recomendación Final</label>
                <div className="ed-btn-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  {(['publicar', 'revisar', 'descartar'] as const).map((rec) => (
                    <button
                      key={rec}
                      type="button"
                      onClick={() => setReportData(prev => ({ ...prev, recommendation: rec }))}
                      className={cn(
                        'ed-badge',
                        reportData.recommendation === rec && (rec === 'publicar' ? 'ed-badge--green' : rec === 'revisar' ? 'ed-badge--amber' : 'ed-badge--red')
                      )}
                      style={{ padding: '0.375rem', textAlign: 'center' }}
                    >
                      {rec}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ed-field">
                <label className="ed-field-label">Notas y Observaciones</label>
                <textarea
                  rows={3}
                  value={reportData.evaluatorNotes}
                  onChange={(e) => { const v = e.target.value; setReportData(prev => ({ ...prev, evaluatorNotes: v })); }}
                  placeholder="Conclusiones narrativas, idoneidad de público y justificación de publicación..."
                  className="ed-textarea"
                />
              </div>

              <div className="ed-btn-row" style={{ justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setIsEditingReport(false)} className="ed-btn ed-btn--outline">Cancelar</button>
                <button type="button" onClick={handleSaveReport} className="ed-btn ed-btn--primary"><Check size={12} /> Guardar Informe</button>
              </div>
            </div>
          ) : hasReport ? (
            <div className="ed-col-stack">
              <div className="ed-grid-2" style={{ gap: '0.5rem' }}>
                <div className="ed-box" style={{ textAlign: 'center' }}>
                  <span className="ed-label">Puntuación Media</span>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-brand-green)', display: 'block', marginTop: '0.25rem' }}>{avgScore} / 5.0</span>
                </div>
                <div className="ed-box" style={{ textAlign: 'center' }}>
                  <span className="ed-label">Recomendación</span>
                  <span className={cn(
                    'ed-badge',
                    project.zeroReader?.report?.recommendation === 'publicar' ? 'ed-badge--green' : project.zeroReader?.report?.recommendation === 'revisar' ? 'ed-badge--amber' : 'ed-badge--red'
                  )} style={{ marginTop: '0.5rem' }}>
                    {project.zeroReader?.report?.recommendation}
                  </span>
                </div>
              </div>

              <div className="ed-box" style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                <div className="ed-row-between"><span className="ed-text-muted">Calidad Narrativa:</span><span className="ed-value">{project.zeroReader?.report?.narrativeQuality} / 5</span></div>
                <div className="ed-row-between"><span className="ed-text-muted">Viabilidad Comercial:</span><span className="ed-value">{project.zeroReader?.report?.commercialViability} / 5</span></div>
                <div className="ed-row-between"><span className="ed-text-muted">Rigor Técnico:</span><span className="ed-value">{project.zeroReader?.report?.technicalRigor} / 5</span></div>
                <div className="ed-row-between"><span className="ed-text-muted">Adecuación Catálogo:</span><span className="ed-value">{project.zeroReader?.report?.catalogFit} / 5</span></div>
              </div>

              <div className="ed-box">
                <span className="ed-label" style={{ marginBottom: '0.25rem' }}>Dictamen Razonado</span>
                <p className="ed-text-muted" style={{ fontSize: '0.5625rem', fontStyle: 'italic', lineHeight: 1.5 }}>"{project.zeroReader?.report?.evaluatorNotes}"</p>
                <span style={{ fontSize: '0.5rem', color: '#64748b', display: 'block', marginTop: '0.5rem' }}>Fecha de emisión: {project.zeroReader?.report?.evaluationDate}</span>
              </div>
            </div>
          ) : (
            <div className="ed-box ed-box--dashed">
              <Clock size={24} style={{ margin: '0 auto 0.5rem', color: '#fbbf24', opacity: 0.7 }} />
              <p style={{ color: '#fbbf24', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.5625rem' }}>Informe de Lectura Pendiente</p>
              <p className="ed-text-muted" style={{ fontSize: '0.5rem', marginTop: '0.25rem' }}>
                El evaluador aún no ha emitido el informe paramétrico. Es obligatorio para deliberar en Mesa Editorial.
              </p>
              <button onClick={() => setIsEditingReport(true)} className="ed-btn ed-btn--amber" style={{ marginTop: '0.75rem' }}>
                <Sliders size={12} /> Completar Informe Ahora
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Columna derecha: Mesa Editorial */}
      <div className="ed-col-4 ed-col-stack">
        <div className="ed-panel">
          <h3 className="ed-panel-title" style={{ marginBottom: '1rem', borderBottom: '1px solid var(--color-brand-blue-lighter)', paddingBottom: '0.75rem' }}>
            <Building2 size={14} style={{ color: 'var(--color-brand-green)' }} />
            2.2 Mesa_Editorial (Comité)
          </h3>

          <div className="ed-col-stack">
            <div className="ed-box">
              <span className="ed-label">Estado del Expediente</span>
              <div className="ed-row-between" style={{ marginTop: '0.25rem' }}>
                <span className={cn(
                  'ed-badge',
                  boardDecision === 'aprobado' ? 'ed-badge--green' : boardDecision === 'rechazado' ? 'ed-badge--red' : boardDecision === 'revision' ? 'ed-badge--amber' : ''
                )}>
                  {boardDecision === 'aprobado' ? 'PTE. DE CONTRATACIÓN (APROBADA)' : boardDecision === 'rechazado' ? 'RECHAZADA EN MESA' : boardDecision === 'revision' ? 'REVISIÓN SOLICITADA' : 'PENDIENTE DE DELIBERACIÓN'}
                </span>
                {project.editorialBoardDecision?.decisionDate && <span style={{ fontSize: '0.5rem', color: '#94a3b8' }}>{project.editorialBoardDecision.decisionDate}</span>}
              </div>
            </div>

            {!canSubmitToBoard ? (
              <div className="ed-alert ed-alert--danger">
                <ShieldAlert size={16} />
                <div><strong>Bloqueo de Validación Estricto:</strong> No se puede enviar un proyecto a la Mesa Editorial sin el Informe de Lectura completado. Complete el informe en la columna central.</div>
              </div>
            ) : (
              <div className="ed-alert ed-alert--success">
                <CheckCircle2 size={14} />
                <span>Informe completado. La Mesa Editorial puede emitir resolución vinculante.</span>
              </div>
            )}

            <div className="ed-field">
              <label className="ed-field-label">Resolución / Acta de la Mesa Editorial</label>
              <textarea
                rows={3}
                disabled={!canSubmitToBoard}
                value={boardDecisionNotes}
                onChange={(e) => { const v = e.target.value; setBoardDecisionNotes(v); }}
                placeholder={canSubmitToBoard ? 'Redacte el acta de la reunión del comité editorial...' : 'Desbloqueable tras informe de lectura...'}
                className="ed-textarea ed-textarea--dark"
              />
            </div>

            <div className="ed-col-stack">
              <button type="button" disabled={!canSubmitToBoard} onClick={() => handleBoardDecision('aprobado')} className="ed-btn ed-btn--primary ed-btn--block">
                <CheckCircle2 size={13} /> Aprobar Obra → Pasar a Pte. Contratación
              </button>
              <div className="ed-btn-row-2">
                <button type="button" disabled={!canSubmitToBoard} onClick={() => handleBoardDecision('revision')} className="ed-btn ed-btn--outline-amber">Solicitar Revisiones</button>
                <button type="button" disabled={!canSubmitToBoard} onClick={() => handleBoardDecision('rechazado')} className="ed-btn ed-btn--outline-red">Rechazar Manuscrito</button>
              </div>
            </div>

            {boardDecision === 'aprobado' && (
              <div className="ed-alert ed-alert--info">
                <Sparkles size={11} />
                <div>El expediente ha sido habilitado para la <strong>Fase 2 (Estudio de Viabilidad y Contratación)</strong>.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
